// WOPermitPanel — Admin card on the WO detail page. Mints (or reuses) a
// private entry-permission link the office texts/emails to the tenant.
// Shows the current permit state (granted? scheduled?), lets the office
// propose windows or set a hard appointment, view the tenant's suggested
// windows, and message the tenant. Full 2-way loop shares data with
// /wo-permit/{token} public page.
import { useEffect, useState, useCallback } from "react";
import { LockKey, Copy, PaperPlaneTilt, CheckCircle, Envelope, ChatText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function WOPermitPanel({ job }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [minting, setMinting] = useState(false);
  const [win, setWin] = useState({ start: "", end: "", label: "", note: "", hard_appointment: false });
  const [msg, setMsg] = useState("");
  const jobId = job?.id;

  const buildFull = (u) => u && !/^https?:\/\//i.test(u) ? `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}` : u;

  const load = useCallback(async () => {
    if (!jobId) return;
    try {
      const r = await api.get(`/jobs/${jobId}/permit-state`);
      setState(r.data);
    } catch { /* silent — not fatal */ }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  async function mint() {
    setMinting(true);
    try {
      const r = await api.post(`/jobs/${jobId}/permit-link`);
      setState(prev => ({ ...(prev || {}), token: r.data.token, url: r.data.url }));
      try {
        await navigator.clipboard.writeText(buildFull(r.data.url));
        toast.success("Permit link copied — paste into your SMS or email");
      } catch { toast.success("Permit link ready — tap Copy"); }
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not mint link")); }
    finally { setMinting(false); }
  }
  async function copy() {
    if (!state?.url) return;
    try { await navigator.clipboard.writeText(buildFull(state.url)); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  }
  async function propose() {
    if (!win.start.trim() || !win.end.trim()) { toast.error("Add start and end times"); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/permit/admin-window`, win);
      toast.success(win.hard_appointment ? "Hard appointment set — tenant notified" : "Window proposed");
      setWin({ start: "", end: "", label: "", note: "", hard_appointment: false });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  }
  async function sendMsg() {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/permit/message`, { text: msg });
      setMsg("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  }

  const url = buildFull(state?.url);
  const smsBody = encodeURIComponent(
    `Hi — we need to enter ${job?.address_display || job?.address || "your unit"} to work on: ${job?.title || "a maintenance item"}. ` +
    `Please tap this link to grant permission and pick a window: ${url || ""}`
  );
  const emailSubj = encodeURIComponent(`Entry permission needed — ${job?.title || `WO #${job?.work_order_number || ""}`}`);
  const emailBody = smsBody;

  if (!jobId) return null;

  return (
    <div className="mt-4 rugged-card p-3 bg-white border-l-4 border-cyan-600" data-testid="wo-permit-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-black uppercase tracking-wider text-cyan-800 flex items-center gap-1.5">
            <LockKey weight="fill" size={13}/> Entry-Permission Link (2-way)
            {state?.granted && <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Granted</span>}
            {state?.accepted_window && <span className="ml-1 text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Scheduled</span>}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
            Text or email a private link so the tenant can grant entry, pick a window, or accept an office-proposed time. Signed audit stamp on grant. <span className="font-bold">Silent — no SMS/email is auto-sent.</span>
          </div>
        </div>
        {!state?.token && (
          <button data-testid="wo-permit-mint-btn" onClick={mint} disabled={minting}
            className="shrink-0 tactile-btn h-10 px-3 font-black uppercase tracking-wider text-xs border-2 border-cyan-700 bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-60">
            {minting ? "…" : "Get Link & Copy"}
          </button>
        )}
      </div>

      {state?.url && (
        <div className="mt-2 flex items-center gap-2">
          <input readOnly value={url} onFocus={e => e.target.select()}
            className="flex-1 min-w-0 h-9 px-2 text-xs font-mono border-2 border-slate-300 bg-slate-50"
            data-testid="wo-permit-url"/>
          <button onClick={copy} className="shrink-0 h-9 px-3 font-black uppercase text-[11px] tracking-wider border-2 border-slate-800 bg-white hover:bg-slate-100" data-testid="wo-permit-copy-btn">
            <Copy size={12} className="inline mr-1"/> Copy
          </button>
          <a href={`sms:?body=${smsBody}`} data-testid="wo-permit-sms-btn"
            className="shrink-0 h-9 px-3 font-black uppercase text-[11px] tracking-wider border-2 border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1">
            <ChatText size={12}/> SMS
          </a>
          <a href={`mailto:?subject=${emailSubj}&body=${emailBody}`} data-testid="wo-permit-email-btn"
            className="shrink-0 h-9 px-3 font-black uppercase text-[11px] tracking-wider border-2 border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1">
            <Envelope size={12}/> Email
          </a>
        </div>
      )}

      {state?.granted && state?.granted_details && (
        <div className="mt-2 rounded bg-emerald-50 border border-emerald-200 p-2 text-[11px]" data-testid="wo-permit-granted-summary">
          <span className="font-black text-emerald-800">Granted by </span>
          <span className="font-mono">{state.granted_details.signature}</span>
          <span className="text-emerald-700"> · {state.granted_details.at?.slice(0, 16).replace("T", " ")}</span>
          {state.granted_details.contact && <span className="text-emerald-700"> · callback {state.granted_details.contact}</span>}
        </div>
      )}

      {(state?.tenant_proposed_windows || []).length > 0 && !state?.accepted_window && (
        <div className="mt-2">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Tenant suggested</div>
          <div className="space-y-1">
            {(state.tenant_proposed_windows || []).map((w, i) => (
              <div key={i} className="text-[11px] bg-white rounded border border-slate-200 p-2" data-testid={`wo-permit-tenant-window-${i}`}>
                <span className="font-bold">{w.label || `Window ${i + 1}`}</span>
                <span className="text-slate-600"> · {w.start} → {w.end}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {state?.accepted_window && (
        <div className="mt-2 rounded bg-emerald-50 border border-emerald-200 p-2 text-[11px]" data-testid="wo-permit-accepted">
          <div className="flex items-center gap-1 text-emerald-800 font-black"><CheckCircle weight="fill" size={12}/> Scheduled</div>
          <div className="mt-0.5 font-bold">{state.accepted_window.label || "Maintenance visit"}</div>
          <div className="text-emerald-700">{state.accepted_window.start} → {state.accepted_window.end}</div>
          {state.accepted_window.source && <div className="text-[10px] text-emerald-600 mt-0.5 italic">via {state.accepted_window.source.replace(/_/g," ")}</div>}
        </div>
      )}

      {(state?.admin_proposed_windows || []).length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase font-black tracking-widest text-cyan-700 mb-1">You proposed</div>
          <div className="space-y-1">
            {state.admin_proposed_windows.map((w, i) => (
              <div key={i} className="text-[11px] bg-cyan-50 rounded border border-cyan-100 p-2" data-testid={`wo-permit-admin-window-${i}`}>
                <span className="font-bold">{w.label || `Window ${i + 1}`}</span>
                <span className="text-slate-700"> · {w.start} → {w.end}</span>
                {w.note && <div className="text-[10px] italic text-slate-500">{w.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {state?.token && !state?.accepted_window && (
        <div className="mt-3 rounded border border-slate-200 p-2" data-testid="wo-permit-propose-form">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Propose window / set hard appointment</div>
          <input value={win.label} onChange={e => setWin({ ...win, label: e.target.value })}
            placeholder="Label (e.g. Tue 2-4 PM)"
            className="w-full h-8 border border-slate-300 rounded px-2 text-xs mb-1" data-testid="wo-permit-win-label"/>
          <div className="grid grid-cols-2 gap-1">
            <input value={win.start} onChange={e => setWin({ ...win, start: e.target.value })}
              placeholder="Start (Tue 2pm)"
              className="h-8 border border-slate-300 rounded px-2 text-xs" data-testid="wo-permit-win-start"/>
            <input value={win.end} onChange={e => setWin({ ...win, end: e.target.value })}
              placeholder="End (Tue 4pm)"
              className="h-8 border border-slate-300 rounded px-2 text-xs" data-testid="wo-permit-win-end"/>
          </div>
          <textarea value={win.note} onChange={e => setWin({ ...win, note: e.target.value })}
            rows={2} placeholder="Optional note"
            className="w-full border border-slate-300 rounded px-2 py-1 text-xs mt-1" data-testid="wo-permit-win-note"/>
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700 mt-1">
            <input type="checkbox" checked={win.hard_appointment}
              onChange={e => setWin({ ...win, hard_appointment: e.target.checked })}
              data-testid="wo-permit-win-hard"/>
            Hard appointment (we're coming) — schedules immediately
          </label>
          <button disabled={busy} onClick={propose}
            className="w-full mt-2 h-9 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white text-xs font-black tracking-wider uppercase rounded"
            data-testid="wo-permit-win-submit">
            {win.hard_appointment ? "Set hard appointment" : "Propose window"}
          </button>
        </div>
      )}

      {state?.token && (
        <div className="mt-3">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Messages</div>
          <div className="space-y-1 max-h-32 overflow-y-auto mb-2">
            {(state.messages || []).map((m, i) => (
              <div key={i} className={`text-[11px] rounded p-1.5 ${m.from === "admin" ? "bg-cyan-50 ml-4" : "bg-slate-100 mr-4"}`} data-testid={`wo-permit-msg-${i}`}>
                <div className="text-[9px] uppercase font-black opacity-60">{m.from}{m.author ? ` · ${m.author}` : ""}</div>
                <div>{m.text}</div>
              </div>
            ))}
            {!(state.messages || []).length && <div className="text-[11px] text-slate-400 italic">No messages yet.</div>}
          </div>
          <div className="flex gap-1">
            <input value={msg} onChange={e => setMsg(e.target.value)} placeholder="Reply to tenant…"
              className="flex-1 h-8 border border-slate-300 rounded px-2 text-xs" data-testid="wo-permit-msg-input"/>
            <button disabled={busy || !msg.trim()} onClick={sendMsg}
              className="h-8 px-2 bg-slate-900 text-white rounded disabled:opacity-40" data-testid="wo-permit-msg-send">
              <PaperPlaneTilt weight="fill" size={13}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
